import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import * as R from 'ramda'
import { toast } from 'react-toastify'
import { makeStyles } from '@material-ui/styles'
import { Box } from '@material-ui/core'
import { SubmitHandler, useForm, useFieldArray, useWatch } from 'react-hook-form'
import { UploadFilesToS3Param, useS3Upload } from '~/hooks'
import { useApi } from '~/store/store'
import { HfDropInput, AddNewDocumentButton, ConfirmDocumentsTabDialog } from '~/components'
import { mutualTheme } from '~/utils/theme'
import { DropInputContainer } from '../../components'
import { ServiceResponse } from '@mutual-libs/shared-types'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { DocumentationActionButtons } from './components'
import {
  Document,
  DocumentsGroup,
  DocumentsGroupType,
  DocumentAnalysisStatus,
  AddDocumentsOnDocumentGroupInput
} from '@mutual-services/account-documents-types'
import { ProposalRisk } from '@mutual-services/credit-score-pj-types'
import {
  getFileType,
  getFileTypes,
  translateDocumentType,
  FetchRequestDocumentsOutput,
  getDocumentDescriptionByType
} from '~/utils/documents'
import { trackEvent } from '~/service/analytics'
import { getOnDocumentationSaveClickAction } from '~/utils/analyticsHelpers'

const useStyles = makeStyles({
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'flex-end',
    [mutualTheme.breakpoints.down('md')]: {
      width: '100%',
      alignSelf: 'center',
      alignContent: 'center'
    }
  },
  button: {
    height: 56,
    fontWeight: 700,
    textTransform: 'none'
  },
  saveButton: {
    marginRight: mutualTheme.spacing(1)
  },
  container: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column'
  }
})

type FormValues = {
  [K in string]: { file: File }[] | null
}

const generateFormInitialValues = (document: ServiceResponse<DocumentsGroup>, docData: FetchRequestDocumentsOutput[]): FormValues => {
  const types = Object.values(DocumentsGroupType)
  const isAdditionalDocument = document.type === DocumentsGroupType.ADDITIONAL
  const filteredDocData = docData.filter(item => isAdditionalDocument ? (document.documents.some(document => document.key === item.key)) : item.type === document.type)

  const initialValues = types.reduce((acc, type) => {
    return {
      ...acc,
      [isAdditionalDocument ? (document.typeName || type) : type]:
        new Array(filteredDocData.length > document.quantity || filteredDocData.length >= 2
          ? filteredDocData.length
          : (document.quantity >= 4 ? 1 : document.quantity)).fill('doc').map(() => { return { file: null } })
    }
  }, {})

  return initialValues as FormValues
}

export type DocumentationTabProps = {
  tabIndex: number,
  isVisible: boolean,
  disableButton: boolean,
  toggleModal: () => void,
  resetTempTab: () => void,
  handleNextClick: () => void,
  onGotoPage: (page: number) => void,
  docData: FetchRequestDocumentsOutput[],
  document: ServiceResponse<DocumentsGroup>,
  documentsList: ServiceResponse<DocumentsGroup[]>,
  tempCurrentTab: { useTempTab: boolean, tempTab: number },
  setUnsentDocs: Dispatch<SetStateAction<{ file: File }[] | null>>,
  updateDocData: (documents: FetchRequestDocumentsOutput[]) => void
}

export const DocumentationTab: React.FC<DocumentationTabProps> = (props) => {
  const { document, tempCurrentTab, docData, tabIndex, updateDocData, onGotoPage, documentsList, setUnsentDocs, toggleModal, isVisible, resetTempTab } = props
  const { type: documentType, typeName, allowedDocumentTypes, _id: documentTabId, typeDescription, documents } = document
  const classes = useStyles()
  const { _id: accountId, onboardingLoanRequest, pjAnalysisStatus } = useAuthAccount()
  const [uploadFilesToS3, uploadFilesToS3State] = useS3Upload()
  const isAdditionalDocument = useMemo(() => documentType === DocumentsGroupType.ADDITIONAL, [documentType])
  const fileTypes = useMemo(() => getFileTypes(allowedDocumentTypes), [allowedDocumentTypes])
  const [changeDocumentsGroupStatus, changeDocumentsGroupStatusState] = useApi('changeDocumentsGroupStatus')
  const [addDocumentOnDocumentGroup, addDocumentOnDocumentGroupState] = useApi('addDocumentOnDocumentGroup')
  const description = useMemo(() => isAdditionalDocument ? (typeDescription || getDocumentDescriptionByType(documentType, pjAnalysisStatus.proposalRisk || ProposalRisk.DENIED, onboardingLoanRequest?.monthlyIncome || 0)) : getDocumentDescriptionByType(documentType, pjAnalysisStatus.proposalRisk || ProposalRisk.DENIED, onboardingLoanRequest?.monthlyIncome || 0), [documentType, isAdditionalDocument, typeDescription])

  const inputQuantity = useMemo(() => document.quantity >= 4 ? 15 : document.quantity, [document])

  const formId = useMemo(() => {
    return `documentationTab-form-${tabIndex}`
  }, [tabIndex])

  const isLoading = useMemo(() => {
    return (
      uploadFilesToS3State.loading ||
      addDocumentOnDocumentGroupState.loading ||
      changeDocumentsGroupStatusState.loading
    )
  }, [addDocumentOnDocumentGroupState.loading, uploadFilesToS3State.loading, changeDocumentsGroupStatusState.loading])

  const { handleSubmit, control, register, getValues } = useForm<FormValues>({
    defaultValues: generateFormInitialValues(document, docData)
  })

  const { append, remove, fields } = useFieldArray({
    control,
    name: isAdditionalDocument ? (typeName || documentType) : documentType
  })

  const watchFieldArray = useWatch({
    control,
    name: isAdditionalDocument ? (typeName || documentType) : documentType
  })

  setUnsentDocs(watchFieldArray)

  const canAddDocuments = useMemo(() => fields.length < inputQuantity, [fields, inputQuantity])

  const getDocumentsParams = useCallback((documents: { file: File }[]): AddDocumentsOnDocumentGroupInput => {
    const formatedDocuments = documents.map(document => {
      return {
        type: documentType,
        originalName: document.file.name,
        mimeType: getFileType(document.file.type)
      }
    })

    return {
      documents: formatedDocuments,
      documentsGroupId: documentTabId
    }
  }, [documentType, documentTabId])

  const onSubmit: SubmitHandler<FormValues> = useCallback(async (formValues: FormValues) => {
    trackEvent(getOnDocumentationSaveClickAction(documentType))
    const currentInput = formValues[isAdditionalDocument ? (typeName || documentType) : documentType]

    if (!currentInput) {
      toast.error('Erro ao salvar os documentos. Tente novamente em alguns instantes.', {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })
      return
    }

    const getInputs = currentInput.filter(item => item.file !== null)

    if (R.isEmpty(getInputs)) {
      toast.warning('Adicione um arquivo a ser enviado ou avance para a próxima aba', {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })
      return
    }

    const paramsToSend = getDocumentsParams(getInputs)
    const addDocumentOnDocumentGroupResult = await addDocumentOnDocumentGroup(paramsToSend)

    if (addDocumentOnDocumentGroupResult.error) {
      toast.error('Erro ao retornar URLs assinadas para realizar upload.', {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })
      return
    }

    const getS3ParamsToSend = (documents: ServiceResponse<Document[]>): UploadFilesToS3Param => {
      const paramsToSend: UploadFilesToS3Param = []

      documents.forEach((document, index) => {
        const currentFile = getInputs[index]

        if (!currentFile) {
          return
        }

        paramsToSend.push({
          file: currentFile.file,
          url: document.uploadUrl
        })
      })

      return paramsToSend
    }

    const s3Params = getS3ParamsToSend(addDocumentOnDocumentGroupResult.data)
    const s3UploadResult = await uploadFilesToS3(s3Params)

    if (s3UploadResult.error) {
      toast.error('Erro ao realizar upload para o S3', {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })
      return
    }

    const changeDocumentsGroupStatusResult = await changeDocumentsGroupStatus({
      accountId,
      denialReason: null,
      documentsGroupId: document._id,
      status: DocumentAnalysisStatus.PENDING_ANALYSIS
    })

    if (changeDocumentsGroupStatusResult.ok) {
      updateDocData(addDocumentOnDocumentGroupResult.data)
      toast.success(`${isAdditionalDocument ? (typeName || translateDocumentType(documentType)) : translateDocumentType(documentType)} enviado com sucesso!`, {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })

      if (isAdditionalDocument) {
        window.location.reload()
        return
      }

      if (isVisible) {
        toggleModal()
      }
      const nextTab = tabIndex + 1
      onGotoPage(tempCurrentTab.useTempTab ? tempCurrentTab.tempTab : nextTab)
      resetTempTab()
      return
    }

    toast.error('Erro ao salvar os documentos. Tente novamente em alguns instantes.', {
      autoClose: 3000,
      closeButton: false,
      position: toast.POSITION.TOP_RIGHT
    })
  }, [addDocumentOnDocumentGroup, tempCurrentTab, updateDocData, onGotoPage, getDocumentsParams,
    isAdditionalDocument, accountId, changeDocumentsGroupStatus, tabIndex, documentType, typeName, uploadFilesToS3,
    document._id, isVisible, toggleModal, resetTempTab])

  const completedAt = useMemo(() => {
    if (isAdditionalDocument) {
      const filteredDocData = docData.filter(document => documents.some(item => item.key === document.key)).map((item, index) => {
        return { ...item, [`${typeName}.${index}`]: `${typeName}.${index}` }
      })

      return filteredDocData.reduce((acc, item, index) => {
        return {
          ...acc,
          [`${typeName}.${index}`]: item.sentAt
        }
      }, {} as any)
    }

    const filteredDocData = docData.filter(document => document.type === documentType).map((item, index) => {
      return { ...item, type: `${item.type}.${index}` }
    })

    return filteredDocData.reduce((acc, item) => {
      return {
        ...acc,
        [item.type]: item.sentAt
      }
    }, {} as any)
  }, [docData, documentType, isAdditionalDocument, documents, typeName])

  const appendInput = useCallback(() => {
    if (fields.length >= inputQuantity) {
      return
    }
    append({ file: null } as any)
  }, [append, fields, inputQuantity])

  const removeInput = useCallback((index: number) => {
    if (fields.length === 1) {
      return
    }

    remove(index)
  }, [remove, fields])

  const uploadDocumentsFromDialog = useCallback(() => {
    const formValues = getValues()

    onSubmit(formValues)
  }, [onSubmit, getValues])

  const onContinueWithoutSaving = useCallback(() => {
    toggleModal()
    setUnsentDocs([])
    onGotoPage(tempCurrentTab.tempTab)
    resetTempTab()
  }, [onGotoPage, setUnsentDocs, toggleModal, tempCurrentTab, resetTempTab])

  return (
    <>
      <Box className={classes.container}>
        {description}

        <DropInputContainer>
          {fields.map((field: any, index) => {
            return (
              <HfDropInput
                key={field.id}
                fileTypes={fileTypes}
                label={typeName || ''}
                control={control as any}
                defaultValue={field.file}
                canRemove={fields.length > 1}
                allowedDocumentTypes={allowedDocumentTypes}
                completedAt={completedAt[`${isAdditionalDocument ? typeName : documentType}.${index}`]}
                fieldArrayProps={{
                  remove: () => removeInput(index)
                }}
                {...register(`${isAdditionalDocument ? typeName : documentType}.${index}.file` as const)}
              />
            )
          })}
          {canAddDocuments && (
            <AddNewDocumentButton
              onClick={appendInput}
              label={typeName || ''}
            />
          )}
        </DropInputContainer>
      </Box>

      <form id={formId} className={classes.buttonContainer} onSubmit={handleSubmit(onSubmit)}>
        <DocumentationActionButtons
          docData={docData}
          isLoading={isLoading}
          documentsList={documentsList}
        />
      </form>
      <ConfirmDocumentsTabDialog
        loading={isLoading}
        onClose={toggleModal}
        isVisible={isVisible}
        onClick={uploadDocumentsFromDialog}
        onCancelClick={onContinueWithoutSaving}
      />
    </>
  )
}
